import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Spinner, useDisclosure } from "@nextui-org/react";
import {useCustom, useCustomMutation, useInvalidate, useUpdate} from "@refinedev/core";
import { debounce } from "lodash";
import React, { FC, useEffect, useMemo } from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { useActiveAssociation } from "../../../context/active-association.context";
import {useDrawer} from "../../../context/drawer.context";
import { ResourceIdentifier } from "../../../resources";
import { AssociationUser, User } from "../../../sdk";
import { FormInput } from "../../ui/form/form-input";
import * as yup from "yup";

export type EmailInputProps = {
  form: UseFormReturn<any>;
  associationUser?: AssociationUser;
  setExistingUser?: (user: User) => void;
};

export const ChangeEmailForm: FC<{
  associationUser?: AssociationUser;
}> = ({ associationUser }) => {
  const schema = yup.object().shape({
    email: yup.string().email("Must be a valid email").required(),
  });
  const invalidate = useInvalidate();
  const { closeDrawer } = useDrawer();

  const activeAssociation = useActiveAssociation();
  const { mutateAsync: updateEmailMutation, isLoading } = useCustomMutation({});

  const form = useForm({
    defaultValues: {
      email: "",
    },
    values: {
      email: "",
    },
    resolver: yupResolver(schema),
  });



  const updateEmail = async (data: yup.InferType<typeof schema>) => {
    await updateEmailMutation({
      url: `${import.meta.env.VITE_PUBLIC_BACKEND_API_URL}/association/${
        activeAssociation?.id
      }/user/${associationUser?.id}/email`,
      method: "post",
      values: {
        email: data.email,
      },
      successNotification: {
        message: "Email updated successfully",
        type: "success",
      },
      errorNotification: {
        message: "Error updating email",
        type: "error",
      },
    });

    form.reset();
    form.clearErrors();

    await invalidate({
      resource: ResourceIdentifier.AssociationUser,
      invalidates: ["all"],
    });
    closeDrawer();
  };

  return (
    <>
      <FormProvider {...form}>
        <form
          noValidate={true}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (form.formState.errors.email) {
              console.log(form.formState.errors.email)
              return;
            }
            form.handleSubmit(updateEmail, (error) => {
              console.log({ error });
            })(e);
        }}
        >
          <div className={"flex flex-col gap-4 mb-6 max-w-lg"}>
            <EmailInput form={form} associationUser={associationUser} />

            <Button
              size={"lg"}
              color={"secondary"}
              type={"submit"}
              fullWidth={true}
              isLoading={isLoading}
              isDisabled={isLoading}
            >
              Save
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export const EmailInput: FC<EmailInputProps> = ({ form }) => {
  const activeAssociation = useActiveAssociation();
  const [emailSearchState, setEmailSearchState] = React.useState("");

  let emailWatch = form.watch("email");

  // Define the debounced function outside useEffect
  const debouncedSetEmailSearchState = useMemo(
    () => debounce((value) => setEmailSearchState(value), 500),
    [] // Empty dependency array means this is created only once
  );

  // Use it in the effect
  useEffect(() => {
    console.log("Debouncing");
    debouncedSetEmailSearchState(emailWatch);

    // Clean up on unmount
    return () => {
      debouncedSetEmailSearchState.cancel();
    };
  }, [emailWatch, debouncedSetEmailSearchState]);

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      debouncedSetEmailSearchState.cancel();
    };
  }, [debouncedSetEmailSearchState]);

  const { data: email, isFetching: isEmailLoading } = useCustom({
    url: `${import.meta.env.VITE_PUBLIC_BACKEND_API_URL}/association/${
      activeAssociation?.id
    }/user`,
    config: {
      filters: [
        {
          field: "user.email",
          operator: "eq",
          value: encodeURIComponent(emailSearchState),
        },
      ],
    },
    method: "get",
  });

  const { data: user, isFetching: isAssociationUserLoading } = useCustom<User>({
    url: `${import.meta.env.VITE_PUBLIC_BACKEND_API_URL}/users/${
      email?.data?.data?.[0]?.id
    }`,
    method: "get",
    queryOptions: {
      enabled: !!email?.data?.data?.[0]?.id,
      queryKey: ["associationUserEmail", email?.data?.data?.[0]?.id],
    },
  });

  useEffect(() => {
    if (emailWatch === "") {
      form.clearErrors("email");
      return;
    }

    if (isAssociationUserLoading || isEmailLoading) {
      return;
    }

    if (email?.data?.data?.length > 0) {
      form.setError("email", {
        type: "custom",
        message: "User with this email already exists",
      });

      if (
        !user?.data?.associationUsers?.some(
          (el) => el.associationId === activeAssociation?.id
        )
      ) {
        onOpenInviteToAssociation();
      }
    } else {
      form.clearErrors("email");
    }
  }, [
    emailWatch,
    user?.data,
    email?.data,
    isEmailLoading,
    isAssociationUserLoading,
  ]);

  const {
    isOpen: isInviteToAssociationOpen,
    onOpen: onOpenInviteToAssociation,
    onOpenChange,
  } = useDisclosure();

  if (!activeAssociation) {
    return null;
  }

  return (
    <FormInput
      name={"email"}
      label={"Email"}
      variant={"bordered"}
      control={form.control}
      endContent={
        <div className={"flex flex-col h-full justify-center"}>
          {(isEmailLoading || isAssociationUserLoading) && (
            <Spinner size={"sm"} />
          )}
        </div>
      }
    />
  );
};
